import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";

import AuthenticateLayout from "../../components/AuthenticateLayout";
import { Loading } from "../../components";
import { getValueByLanguage } from "../../utils/utils";
import ChangeLanguage from "../../components/ChangeLanguage";
import StepsPoint from "../../components/StepsPoint";
import { Footer } from "../../components/Footer";

import { useEngage } from "./hooks/useEngage";
import { useCustomHeadStyles } from "../../hooks/useCustomHeadStyles";
import { EngageWrapper, StepsContainer } from "./styles";
import ErrorNotification from "./components/ErrorNotification";
import SubmitButtonArea from "./components/SubmitButtonArea";
import ProductPriceInput from "./components/ProductPriceInput";
import { ENV } from "../../configs/env";

const Engage: React.FC = () => {
  const { i18n } = useTranslation();
  const {
    data,
    errorMessage,
    buttonDisabled,
    isLoading,
    isSkipCampaignPage,
    getYouGift,
    goToRewardList,
    isNonVendingPayGetCoupon,
    productPrice,
    setProductPrice,
    isProductPriceEditable,
    skipCampaignPage
  } = useEngage();

  useCustomHeadStyles(data?.custom_html);

  // Derived data for display
  const steps = useMemo(
    () => {
      const note = getValueByLanguage(data, "engagement_note");
      return data?.languages && note ? JSON.parse(note) : [];
    },
    [data, i18n.language]
  );

  const instructionImageValue = getValueByLanguage(data, "instructions_image");
  const bannerTopperImage = getValueByLanguage(data, "campaign_page_banner_topper_image");
  const customHtml = getValueByLanguage(data, "custom_html_engage");
  const pageAttentionTooltipTitle = getValueByLanguage(data, "campaign_page_attention_tooltip_title");

  // Helper flags
  const showLoading = isLoading || isSkipCampaignPage || skipCampaignPage;
  const showCustomHtml = !!customHtml;
  const showInstructionImage = !customHtml && !!instructionImageValue;

  const handleContinue = (isFromContinueBtn?: boolean) => {
    getYouGift(
      data?.id,
      data?.url,
      data?.is_click_campaign || data.subcampaign_type_name === "SMART_ENGAGE_CLICK",
      data?.customer_id,
      isFromContinueBtn
    );
  };

  return (
    <EngageWrapper>
      {showLoading && <Loading customBackgroundColor="rgba(255,255,255,1)" />}
      <AuthenticateLayout
        button={() => !ENV.HIDE_TRANSLATOR && <ChangeLanguage />}
        bannerTopperImage={!showCustomHtml && bannerTopperImage}
        background={data?.advertiser_banner}
        isLoading={showLoading}
        bannerImageCorner={data?.banner_image_corner}
        defaultBackground={""}
        hideLogo={ENV.HIDE_LOGO}
      >
        {showInstructionImage && (
          <img
            src={instructionImageValue}
            alt="instruction"
            className="banner"
          />
        )}

        {showCustomHtml && (
          <div
            style={{ padding: "0px" }}
            className="ql-editor mobile-preview-content"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(customHtml)
            }}
          />
        )}

        {errorMessage ? (
          <ErrorNotification message={errorMessage} />
        ) : (
          <>
            {isNonVendingPayGetCoupon && (
              <ProductPriceInput
                value={productPrice}
                onChange={setProductPrice}
                isEditable={isProductPriceEditable}
              />
            )}

            <SubmitButtonArea
              errorMessage={errorMessage}
              pageAttentionTooltipTitle={pageAttentionTooltipTitle}
              buttonDisabled={buttonDisabled}
              onContinue={handleContinue}
              onGoToRewards={goToRewardList}
            />

            <StepsContainer>
              <StepsPoint steps={steps} />
            </StepsContainer>
          </>
        )}

        {!errorMessage && <Footer />}
      </AuthenticateLayout>
    </EngageWrapper>
  );
};

export default Engage;
